import { Link } from '@remix-run/react'
import FocusTrap from 'focus-trap-react'
import { motion, AnimatePresence } from 'framer-motion'
import * as React from 'react'
import { Button } from '#app/components/ui/button'
import { useRequestInfo } from '#app/utils/request-info'
import { useOptionalUser } from '#app/utils/user'

const NAV_LINKS = [
	{ name: 'About', to: '/about' },
	{ name: 'How it works', to: '/#how-it-works' },
	{ name: 'Pricing', to: '/pricing' },
	{ name: 'Support', to: '/support' },
]
const topVariants = {
	open: { rotate: 45, y: 7, originX: '16px', originY: '10px' },
	closed: { rotate: 0, y: 0, originX: 0, originY: 0 },
}
const centerVariants = {
	open: { opacity: 0 },
	closed: { opacity: 1 },
}
const bottomVariants = {
	open: { rotate: -45, y: -5, originX: '16px', originY: '22px' },
	closed: { rotate: 0, y: 0, originX: 0, originY: 0 },
}
export function Navbar() {
	const user = useOptionalUser()
	const [isMobileNavOpen, setIsMobileNavOpen] = React.useState(false)
	const {
		hints: { reducedMotion },
	} = useRequestInfo()
	const isReducedMotion = reducedMotion === 'reduce'
	const transition = isReducedMotion ? { duration: 0 } : {}

	const toggleMobileNav = () => {
		setIsMobileNavOpen(prevIsMobileNavOpen => !prevIsMobileNavOpen)
	}
	const navLinks = [
		...NAV_LINKS,
		...(user ? [{ name: 'Dashboard', to: '/app/dashboard' }] : []),
	]

	React.useEffect(() => {
		if (isMobileNavOpen) {
			// don't use overflow-hidden, as that toggles the scrollbar and causes layout shift
			document.body.classList.add('fixed')
			document.body.classList.add('w-full')
			document.body.classList.add('overflow-y-hidden')
		} else {
			document.body.classList.remove('fixed')
			document.body.classList.remove('w-full')
			document.body.classList.remove('overflow-y-hidden')
		}
	}, [isMobileNavOpen])

	return (
		<>
			{/* desktop nav */}
			<div className="hidden items-center gap-10 md:flex">
				{NAV_LINKS.map(({ name, to }) => (
					<Link to={to} key={to} className="underlined">
						{name}
					</Link>
				))}
			</div>
			{/* mobile nav */}
			<FocusTrap active={isMobileNavOpen}>
				<div className="md:hidden">
					<motion.button
						initial="hide"
						animate={isMobileNavOpen ? 'open' : 'closed'}
						onClick={toggleMobileNav}
						className="relative z-40 flex flex-col space-y-1"
					>
						<svg
							width="32"
							height="32"
							viewBox="0 0 32 32"
							fill="none"
							xmlns="http://www.w3.org/2000/svg"
						>
							<motion.rect
								variants={topVariants}
								transition={transition}
								x="6"
								y="9"
								width="20"
								height="2"
								rx="1"
								fill="currentColor"
							/>
							<motion.rect
								variants={centerVariants}
								transition={transition}
								x="6"
								y="15"
								width="20"
								height="2"
								rx="1"
								fill="currentColor"
							/>
							<motion.rect
								variants={bottomVariants}
								transition={transition}
								x="6"
								y="21"
								width="20"
								height="2"
								rx="1"
								fill="currentColor"
							/>
						</svg>
						<span className="sr-only">
							{isMobileNavOpen ? 'Close' : 'Open'} navbar
						</span>
					</motion.button>
					<AnimatePresence>
						{isMobileNavOpen && (
							<motion.div
								variants={{
									closed: {
										y: -50,
										opacity: 0,
									},
									open: {
										y: 0,
										opacity: 1,
									},
								}}
								transition={{
									duration: isReducedMotion ? 0 : 0.15,
									ease: 'linear',
								}}
								initial="closed"
								animate="open"
								exit="closed"
								className="fixed inset-0 z-30 bg-background pt-20 md:hidden"
								role="dialog"
								tabIndex={-1}
							>
								<ul className="border-t border-secondary">
									{navLinks.map(({ name, to }) => (
										<li key={to} className="w-full border-b border-secondary">
											<Link
												to={to}
												onClick={toggleMobileNav}
												className="block w-full px-6 py-8 hover:bg-secondary focus-visible:bg-secondary"
											>
												{name}
											</Link>
										</li>
									))}
									<div
										className="mt-5 flex space-x-3 px-6"
										onClick={() => setIsMobileNavOpen(false)}
									>
										{user ? null : (
											<>
												<li className="w-full">
													<Button asChild variant="outline" className="w-full">
														<Link to="/login">Log In</Link>
													</Button>
												</li>
												<li className="w-full">
													<Button
														asChild
														variant="secondary"
														className="w-full"
													>
														<Link to="/signup">Sign up</Link>
													</Button>
												</li>
											</>
										)}
									</div>
								</ul>
							</motion.div>
						)}
					</AnimatePresence>
				</div>
			</FocusTrap>
		</>
	)
}
